import { THEMES, type Theme } from "./theme";
import { LAYER_REGISTRY, isLayerName, isMeasureOfLayer } from "../map/layers/registry";
import type { LayerName, MeasureId } from "../map/layers/types";

export type ViewState = {
  theme: Theme;
  center: [lat: number, lng: number];
  zoom: number;
  layers: LayerName[];
  measures: Partial<Record<LayerName, MeasureId>>; // omitted entries fall back to defaultMeasure
  entity: string | null;
};

export const DEFAULT_VIEW: ViewState = {
  theme: "amber",
  center: [45.508, -73.554],
  zoom: 12,
  layers: [],
  measures: {},
  entity: null,
};

function isTheme(v: string): v is Theme {
  return (THEMES as readonly string[]).includes(v);
}

export function readQuery(search: string = window.location.search): Partial<ViewState> {
  const p = new URLSearchParams(search);
  const out: Partial<ViewState> = {};
  const t = p.get("t");
  if (t && isTheme(t)) out.theme = t;
  const c = p.get("c");
  if (c !== null && c !== "") {
    const [latStr, lngStr] = c.split(",");
    if (latStr !== undefined && latStr !== "" && lngStr !== undefined && lngStr !== "") {
      const lat = Number(latStr);
      const lng = Number(lngStr);
      if (Number.isFinite(lat) && Number.isFinite(lng)) out.center = [lat, lng];
    }
  }
  const z = p.get("z");
  if (z !== null && z !== "") {
    const n = Number(z);
    if (Number.isFinite(n)) out.zoom = n;
  }
  const layers = p.get("layers");
  if (layers !== null) {
    const parsed = layers.split(",").filter((s) => s.length > 0).filter((s) => {
      if (isLayerName(s)) return true;
      if (import.meta.env.DEV) console.warn(`router: unknown layer in layers=: ${s}`);
      return false;
    }) as LayerName[];
    out.layers = parsed;
  }
  const m = p.get("m");
  {
    const measures: Partial<Record<LayerName, MeasureId>> = {};
    for (const token of (m ?? "").split(",")) {
      if (!token) continue;
      const layerPart = token.split(".")[0] ?? "";
      if (!isLayerName(layerPart)) {
        if (import.meta.env.DEV) console.warn(`router: unknown layer in m=: ${token}`);
        continue;
      }
      if (!isMeasureOfLayer(token, layerPart)) {
        if (import.meta.env.DEV) console.warn(`router: unknown measure in m=: ${token}`);
        continue;
      }
      measures[layerPart] = token as MeasureId;
    }
    out.measures = measures;
  }
  const e = p.get("e");
  if (e !== null) out.entity = e;
  return out;
}

export function writeQuery(state: Partial<ViewState>, replace = false): void {
  const p = new URLSearchParams(window.location.search);
  if (state.theme !== undefined) p.set("t", state.theme);
  if (state.center !== undefined) p.set("c", `${state.center[0]},${state.center[1]}`);
  if (state.zoom !== undefined) p.set("z", String(state.zoom));
  if (state.layers !== undefined) p.set("layers", state.layers.join(","));
  if (state.measures !== undefined) {
    const tokens: string[] = [];
    for (const layerName of Object.keys(state.measures) as LayerName[]) {
      const measure = state.measures[layerName];
      if (!measure) continue;
      const defaultMeasure = LAYER_REGISTRY[layerName].defaultMeasure;
      if (measure === defaultMeasure) continue; // omit defaults from URL
      tokens.push(measure);
    }
    if (tokens.length > 0) p.set("m", tokens.join(","));
    else p.delete("m");
  }
  if (state.entity !== undefined) {
    if (state.entity === null) p.delete("e");
    else p.set("e", state.entity);
  }
  const q = p.toString();
  const url = q.length > 0 ? `?${q}` : window.location.pathname;
  if (replace) window.history.replaceState({}, "", url);
  else window.history.pushState({}, "", url);
}

const NAV_EVENT = "bcdl:navigate";

export function navigate(pathAndSearch: string, replace = false): void {
  if (replace) history.replaceState({}, "", pathAndSearch);
  else history.pushState({}, "", pathAndSearch);
  window.dispatchEvent(new Event(NAV_EVENT));
}

export function onNavigate(cb: () => void): () => void {
  const handler = () => cb();
  window.addEventListener(NAV_EVENT, handler);
  window.addEventListener("popstate", handler);
  return () => {
    window.removeEventListener(NAV_EVENT, handler);
    window.removeEventListener("popstate", handler);
  };
}
